import React from 'react';

import { Nullable } from '@clearverify/types';

import {
  PeoStaffDocument,
  PeoStaffRole,
  PeoStaffUpdate,
  usePeoStaffQuery,
  usePeoStaffUpdateMutation,
} from 'src/api';
import { PEO_ROLE_OPTIONS } from 'src/constants/peo';

export const usePeoStaff = (peoStaffId: Nullable<string>) => {
  const { data, loading } = usePeoStaffQuery({
    variables: {
      id: peoStaffId || '',
    },
    skip: !peoStaffId,
  });

  const [peoStaffUpdateMutation, { loading: updating }] = usePeoStaffUpdateMutation({
    refetchQueries: [PeoStaffDocument],
    awaitRefetchQueries: true,
  });

  const peoStaff = React.useMemo(() => data?.peoStaff, [data?.peoStaff]);

  const isPeoSuperAdmin = React.useMemo(
    () => peoStaff?.role === PeoStaffRole.PeoSuperAdmin,
    [peoStaff?.role],
  );

  const peoStaffRoleName = React.useMemo(() => {
    return PEO_ROLE_OPTIONS.find(role => role.value === peoStaff?.role)?.label;
  }, [peoStaff?.role]);

  const updatePeoStaff = React.useCallback(
    async (
      data: PeoStaffUpdate,
      options?: Omit<Parameters<typeof peoStaffUpdateMutation>[0], 'variables'>,
    ) => {
      if (!peoStaffId) {
        return;
      }

      await peoStaffUpdateMutation({
        variables: {
          where: { id: peoStaffId },
          data,
        },
        ...options,
      });
    },
    [peoStaffId, peoStaffUpdateMutation],
  );

  return {
    peoStaff,
    isPeoSuperAdmin,
    peoStaffRoleName,
    updatePeoStaff,
    loading,
    updating,
  };
};
